import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Edit, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { LoadingZoneAdmin } from '../../../../types/loadingZone';
import { loadingZoneService } from '../../../../services/loadingZoneService';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils';
import type { ColumnDefinition, TableAction } from '../../../shared/table/types/table';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface LoadingZoneAppProps {
  onClose?: () => void;
  onBack?: () => void;
}

interface LoadingZoneFormData {
  loadingZoneID: string;
  description1: string;
}

const emptyFormData: LoadingZoneFormData = { loadingZoneID: '', description1: '' };

/**
 * P9 — Lastesone-vedlikeholdsmodul. CRUD for LoadingZone, bygget etter pack-zones-mønsteret
 * (WarehouseTable + modal). Lastesonene refereres av regelmotor-presetet (Export/NVIT) og
 * av lastesone-dropdownen i pakke-/forsendelsesvisningen.
 */
export const LoadingZoneApp: React.FC<LoadingZoneAppProps> = ({ onClose, onBack }) => {
  const { t } = useTranslation();
  const [zones, setZones] = useState<LoadingZoneAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<LoadingZoneFormData>(emptyFormData);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadZones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await loadingZoneService.getLoadingZones();
      setZones(data);
    } catch (err) {
      console.error('Error loading loading zones:', err);
      setError(t('manager.modules.adminDelivery.loadingZone.errors.loadFailed', {}, 'Feil ved lasting av lastesoner'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  const handleSelectionChange = useCallback((ids: (string | number)[], _data: LoadingZoneAdmin[]) => {
    setSelectedIds(ids.map((id) => String(id)));
  }, []);

  const getRowId = useCallback((item: LoadingZoneAdmin) => item.id, []);

  const openCreate = useCallback(() => {
    setEditingId(null);
    setFormData(emptyFormData);
    setFormError(null);
    setSuccessMessage(null);
    setShowFormModal(true);
  }, []);

  const openEdit = useCallback(() => {
    if (selectedIds.length !== 1) return;
    const row = zones.find((z) => String(z.id) === selectedIds[0]);
    if (!row) return;
    setEditingId(row.id);
    setFormData({ loadingZoneID: String(row.loadingZoneID), description1: row.description1 });
    setFormError(null);
    setSuccessMessage(null);
    setShowFormModal(true);
  }, [selectedIds, zones]);

  const handleDelete = useCallback(async () => {
    if (selectedIds.length === 0) return;
    const confirmed = window.confirm(
      t('manager.modules.adminDelivery.loadingZone.confirmDelete', { '0': selectedIds.length }, `Slette ${selectedIds.length} lastesone(r)?`)
    );
    if (!confirmed) return;
    try {
      for (const id of selectedIds) {
        await loadingZoneService.deleteLoadingZone(parseInt(id, 10));
      }
      await loadZones();
      setSelectedIds([]);
    } catch (err) {
      console.error('Error deleting loading zones:', err);
      setError(t('manager.modules.adminDelivery.loadingZone.errors.deleteFailed', {}, 'Feil ved sletting av lastesone'));
    }
  }, [selectedIds, loadZones, t]);

  const columnFactory = createColumnFactory<LoadingZoneAdmin>();

  const columns: ColumnDefinition<LoadingZoneAdmin>[] = useMemo(() => [
    columnFactory.createNumberColumn('loadingZoneID', 'loadingZoneID', t('manager.modules.adminDelivery.loadingZone.columns.loadingZoneID', {}, 'Lastesone ID'), {
      width: 140, sortable: true, filterType: 'text'
    }),
    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      width: 280, sortable: true, filterType: 'text'
    }),
    columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.adminDelivery.common.columns.createdBy', {}, 'Opprettet av'), {
      width: 120, sortable: true, filterType: 'text'
    }),
    columnFactory.createDateColumn('created', 'created', t('manager.modules.adminDelivery.common.columns.created', {}, 'Opprettet'), {
      width: 130, sortable: true, filterType: 'text'
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.adminDelivery.common.columns.updatedBy', {}, 'Oppdatert av'), {
      width: 120, sortable: true, filterType: 'text'
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.adminDelivery.common.columns.lastUpdate', {}, 'Sist oppdatert'), {
      width: 130, sortable: true, filterType: 'text'
    })
  ], [t]);

  const tableActions: TableAction<LoadingZoneAdmin>[] = useMemo(() => [
    {
      id: 'create-loadingzone',
      label: t('manager.modules.adminDelivery.loadingZone.actions.createNew', {}, 'Opprett ny'),
      icon: Plus,
      onClick: openCreate,
      variant: 'primary' as const
    },
    {
      id: 'edit-loadingzone',
      label: t('common.buttons.edit', {}, 'Rediger'),
      icon: Edit,
      onClick: openEdit,
      disabled: () => selectedIds.length !== 1,
      variant: 'primary' as const
    },
    {
      id: 'delete-loadingzone',
      label: t('manager.modules.adminDelivery.common.delete', { '0': selectedIds.length }, `Slett (${selectedIds.length})`),
      icon: Trash2,
      onClick: handleDelete,
      disabled: () => selectedIds.length === 0,
      variant: 'secondary' as const
    },
    {
      id: 'refresh-loadingzones',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => loadZones(),
      variant: 'secondary' as const
    }
  ], [selectedIds.length, openCreate, openEdit, handleDelete, loadZones, t]);

  const handleInputChange = (field: keyof LoadingZoneFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const zoneIdNum = parseInt(formData.loadingZoneID.trim(), 10);
    if (!formData.loadingZoneID.trim() || isNaN(zoneIdNum)) {
      setFormError(t('manager.modules.adminDelivery.loadingZone.errors.idRequired', {}, 'Lastesone ID (tall) er påkrevd'));
      return;
    }
    if (!formData.description1.trim()) {
      setFormError(t('manager.modules.adminDelivery.loadingZone.errors.descriptionRequired', {}, 'Beskrivelse er påkrevd'));
      return;
    }

    setFormLoading(true);
    setFormError(null);
    try {
      if (editingId == null) {
        await loadingZoneService.createLoadingZone({ loadingZoneID: zoneIdNum, description1: formData.description1.trim() });
        setSuccessMessage(t('manager.modules.adminDelivery.loadingZone.create.success', {}, 'Ny lastesone ble opprettet'));
      } else {
        await loadingZoneService.updateLoadingZone(editingId, { loadingZoneID: zoneIdNum, description1: formData.description1.trim() });
        setSuccessMessage(t('manager.modules.adminDelivery.loadingZone.update.success', {}, 'Lastesone ble oppdatert'));
      }
      setTimeout(() => {
        loadZones();
        setSelectedIds([]);
        setShowFormModal(false);
        setSuccessMessage(null);
      }, 800);
    } catch (err) {
      console.error('Error saving loading zone:', err);
      setFormError(err instanceof Error ? err.message : t('manager.modules.adminDelivery.loadingZone.errors.saveFailed', {}, 'Feil ved lagring av lastesone'));
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white border-b">
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack || (() => {})} />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{t('manager.modules.adminDelivery.loadingZone.title', {}, 'Lastesoner')}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('manager.modules.adminDelivery.loadingZone.subtitle', {}, 'Administrer lastesoner for forsendelse')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button onClick={onClose} className="px-3 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600">
              {t('common.buttons.close', {}, 'Lukk')}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={zones}
          columns={columns}
          loading={loading}
          error={error || undefined}
          config={{
            persistenceKey: 'loadingzone-table',
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 100,
            compactMode: true,
            stickyHeader: true,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: { mode: 'multi', selectAllMode: 'current-page' }
          }}
          selectedRows={selectedIds}
          onSelectionChange={handleSelectionChange}
          getRowId={getRowId}
          tableActions={tableActions}
          aria-label={t('manager.modules.adminDelivery.loadingZone.tableAriaLabel', {}, 'Lastesoner tabell')}
        />
      </div>

      {/* Create/Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId == null
                  ? t('manager.modules.adminDelivery.loadingZone.create.title', {}, 'Opprett ny lastesone')
                  : t('manager.modules.adminDelivery.loadingZone.update.title', {}, 'Rediger lastesone')}
              </h2>
              <button onClick={() => setShowFormModal(false)} className="text-gray-400 hover:text-gray-600 text-xl" aria-label={t('manager.modules.adminDelivery.common.closeModal', {}, 'Lukk modal')}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">{formError}</p>
                </div>
              )}
              {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-800 text-sm">{successMessage}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.adminDelivery.loadingZone.fields.loadingZoneIDRequired', {}, 'Lastesone ID *')}
                </label>
                <input
                  type="number"
                  value={formData.loadingZoneID}
                  onChange={(e) => handleInputChange('loadingZoneID', e.target.value)}
                  disabled={formLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  min={0}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.fields.description', {}, 'Beskrivelse')} *</label>
                <input
                  type="text"
                  value={formData.description1}
                  onChange={(e) => handleInputChange('description1', e.target.value)}
                  disabled={formLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  maxLength={255}
                  required
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowFormModal(false)} disabled={formLoading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50">
                  {t('common.buttons.cancel', {}, 'Avbryt')}
                </button>
                <button type="submit" disabled={formLoading} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {formLoading
                    ? t('manager.modules.adminDelivery.common.saving', {}, 'Lagrer...')
                    : t('common.buttons.save', {}, 'Lagre')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
